import { authClient } from '@/config/auth-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ADMIN_QUERY_KEYS } from '@/api/queries/admin';
import { toast } from '@/components/ui/toast';

interface ListUsersParams {
  page?: number;
  perPage?: number;
  searchValue?: string;
}

/**
 * Query hook for listing users via Better Auth admin client
 */
export function useAdminListUsers(params: ListUsersParams = {}) {
  const { page = 1, perPage = 20, searchValue } = params;

  return useQuery({
    queryKey: [...ADMIN_QUERY_KEYS.users(), { page, perPage, searchValue }],
    queryFn: async () => {
      const query: Record<string, any> = {
        limit: perPage,
        offset: (page - 1) * perPage,
      };
      if (searchValue) {
        query.searchValue = searchValue;
        query.searchField = 'email';
      }
      const result = await authClient.admin.listUsers({ query });
      if (result.error) {
        throw new Error(result.error.message || 'Failed to list users');
      }
      return result.data;
    },
    staleTime: 1000 * 60 * 2,
    meta: { persist: false },
  });
}

/**
 * Mutation hook for setting a user's role
 */
export function useAdminSetRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: 'user' | 'admin' }) => {
      const result = await authClient.admin.setRole({
        userId,
        role,
      });
      if (result.error) {
        throw new Error(result.error.message || 'Failed to set role');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.users() });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to set role');
    },
  });
}

/**
 * Mutation hook for banning a user
 */
export function useAdminBanUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const result = await authClient.admin.banUser({
        userId,
      });
      if (result.error) {
        throw new Error(result.error.message || 'Failed to ban user');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.users() });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to ban user');
    },
  });
}

/**
 * Mutation hook for unbanning a user
 */
export function useAdminUnbanUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const result = await authClient.admin.unbanUser({
        userId,
      });
      if (result.error) {
        throw new Error(result.error.message || 'Failed to unban user');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.users() });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to unban user');
    },
  });
}
